/* eslint-disable no-unused-vars */
import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import genericStyles from "../../Styles/genericStyles.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import GenericModal from "../GenericModal";
import { useContext, useState } from "react";
import { Delete, Save } from "@mui/icons-material";
import { ETableEditAction } from "../../Utils/Enums";
import { SubtaskValidationSchema } from "../../Utils/Models";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getNumberOfLinesFromText, onFormError } from "../../Utils/Utils";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import styles from "../../Styles/task.module.css";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { RefreshDataContext } from "../../Context/RefreshDataContext";
import { updateData } from "../../Services/updateData";
import { AxiosResponse } from "axios";
import { SubtasksModalProps } from "../../Utils/Types";

function SubtasksModal(props: SubtasksModalProps) {
  const memo = useCustomMemo();
  const refreshContext = useContext(RefreshDataContext);
  const taskStatuses = memo?.storage.taskStatuses || [];
  const { register, formState, control, getValues, handleSubmit, reset } =
    useForm<any>({
      resolver: yupResolver(SubtaskValidationSchema),
      defaultValues: {
        status: null,
        text: "",
        description: "",
      },
      mode: "onChange",
      reValidateMode: "onChange",
      context: undefined,
      shouldFocusError: true,
      shouldUnregister: false,
      criteriaMode: "firstError",
    });
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();

  /**
   *
   * @returns
   */
  const getTableOptions = () => {
    return {
      download: false,
      serverSide: true,
      pagination: false,
      print: false,
      viewColumns: false,
      search: false,
      filter: false,
      selectableRows: "none",
      textLabels: {
        body: {
          noMatch: Vocabulary.noResultsFound,
        },
      },
    };
  };

  /**
   *
   */
  const subtasksHeader = [
    {
      label: Vocabulary.crtNo,
      name: "",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return meta.rowIndex + 1;
        },
      },
    },
    {
      label: Vocabulary.name,
      name: "text",
      options: {
        sort: false,
      },
    },
    {
      label: Vocabulary.status,
      name: "status",
      options: {
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return <p>{value ? value.name : "-"}</p>;
        },
      },
    },
  ];

  /**
   *
   * @param model
   */
  const handleAddSubtasks = (model: any) => {
    postData(`${endpoints.task.subtask}/${props.parentTaskId}`, model).then(
      (res: AxiosResponse) => {
        if (res && !res?.data?.errors) {
          emptyFields();
          setTriggerReload(!triggerReload);
          refreshContext.setRefresh(true);
        }
      }
    );
  };

  /**
   *
   * @param model
   */
  const handleUpdateSubtask = (model: any) => {
    updateData(`${endpoints.task.main}/${model._id}`, model).then(
      (res: AxiosResponse) => {
        if (res && !res?.data?.errors) {
          emptyFields();
          setTriggerReload(!triggerReload);
          refreshContext.setRefresh(true);
        }
      }
    );
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    model._id ? handleUpdateSubtask(model) : handleAddSubtasks(model);
  };

  /**
   *
   */
  const handleOnCloseModal = () => {
    props.onClose();
    emptyFields();
  };

  /**
   *
   */
  const emptyFields = () => {
    reset({
      text: "",
      status: null,
      description: "",
    });
  };

  /**
   *
   * @param data
   * @returns
   */
  const handleInlineForm = (data?: any) => {
    if (data) {
      reset(data);
    } else {
      emptyFields();
    }
  };

  return (
    <GenericModal
      open={props.open}
      onClose={handleOnCloseModal}
      title={Vocabulary.subtasks}
      className={styles.subtaskModal}
      children={
        <form
          onSubmit={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit, onFormError)(e);
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                {...register("text")}
                id="text"
                name="text"
                fullWidth
                label={Vocabulary.fancyName}
                multiline
                rows={getNumberOfLinesFromText(getValues("text"))}
                error={typeof formState.errors.text === "object"}
                helperText={formState.errors.text?.message?.toString()}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="status"
                control={control}
                defaultValue={undefined}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    freeSolo={false}
                    disablePortal
                    noOptionsText={Vocabulary.noResultsFound}
                    onChange={(event, value) => field.onChange(value)}
                    value={getValues("status") || null}
                    options={taskStatuses}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    fullWidth
                    getOptionLabel={(option: any) => {
                      return option?.name || option;
                    }}
                    id="status"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        name="status"
                        inputRef={ref}
                        label={Vocabulary.chooseStatus}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: !getValues("status") ? null : (
                            <div
                              className={genericStyles.colorCircle}
                              style={{
                                backgroundColor: getValues("status")?.color,
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("description")}
                id="description"
                name="description"
                fullWidth
                multiline
                rows={4}
                label={Vocabulary.description}
                error={typeof formState.errors.description === "object"}
                helperText={formState.errors.description?.message?.toString()}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={7}>
              <Button
                startIcon={<Save />}
                variant="contained"
                type="submit"
                fullWidth
              >
                {getValues("_id")
                  ? Vocabulary.editSubtask
                  : Vocabulary.addNewSubtask}
              </Button>
            </Grid>
            <Grid item xs={5}>
              <Button
                startIcon={<Delete />}
                variant="contained"
                fullWidth
                onClick={emptyFields}
                color="header"
              >
                {Vocabulary.emptyFields}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <MUIDataTableCustomStyle
                onEditAction={ETableEditAction.INLINE}
                title={`${Vocabulary.subtasks}`}
                handleInlineForm={handleInlineForm}
                CRUDEndPoint={`${endpoints.task.subtask}/${props.parentTaskId}`}
                columns={subtasksHeader}
                options={getTableOptions()}
                triggerReload={triggerReload}
              />
            </Grid>
          </Grid>
        </form>
      }
    />
  );
}

export default SubtasksModal;
